import { useEffect, useState, type FormEvent } from 'react';
import { useLocation } from 'wouter';
import { Copy, KeyRound, Lock, Plus, Unlink } from 'lucide-react';
import { ConfirmDeleteButton } from '../components/confirm-delete-button';
import { toast } from 'sonner';
import {
  useApiKeys,
  useChangePassword,
  useCreateApiKey,
  useDisconnectGoogle,
  useGoogleStatus,
  useRevokeApiKey,
} from '../lib/hooks';
import { Modal } from '../components/modal';

export function SettingsPage() {
  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6 lg:p-8">
      <header>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900 dark:text-slate-100">
          Cài đặt
        </h1>
        <p className="mt-1 text-sm text-slate-500">
          Đổi mật khẩu, quản lý API keys, kết nối Google.
        </p>
      </header>

      <ChangePasswordSection />
      <GoogleSection />
      <ApiKeysSection />
    </div>
  );
}

function GoogleSection() {
  const status = useGoogleStatus();
  const disconnect = useDisconnectGoogle();
  const [location] = useLocation();

  // Surface ?google_connected / ?google_error from callback redirect
  useEffect(() => {
    const url = new URL(window.location.href);
    const connected = url.searchParams.get('google_connected');
    const error = url.searchParams.get('google_error');
    if (connected) {
      toast.success(`✓ Google đã kết nối: ${connected}`);
      url.searchParams.delete('google_connected');
      window.history.replaceState({}, '', url.toString());
    } else if (error) {
      toast.error(`Google: ${error}`);
      url.searchParams.delete('google_error');
      window.history.replaceState({}, '', url.toString());
    }
    void location;
  }, [location]);

  const onConnect = () => {
    window.location.href = '/api/v1/auth/google/start';
  };

  const onDisconnect = () => {
    disconnect.mutate(undefined, {
      onSuccess: () => toast.success('Đã ngắt kết nối'),
      onError: (e) => toast.error(e instanceof Error ? e.message : 'Không ngắt được'),
    });
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title flex items-center gap-2">
          <LinkIconSafe />
          Google (Gmail + Calendar)
        </h2>
      </div>
      <div className="panel-body">
        {!status.data?.configured ? (
          <p className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
            ⚠️ Server chưa cấu hình Google OAuth. Cần admin set{' '}
            <code className="rounded bg-amber-200/50 px-1 dark:bg-amber-500/20">
              GOOGLE_OAUTH_CLIENT_ID
            </code>{' '}
            +{' '}
            <code className="rounded bg-amber-200/50 px-1 dark:bg-amber-500/20">
              GOOGLE_OAUTH_CLIENT_SECRET
            </code>{' '}
            trong .env trước.
          </p>
        ) : status.data?.connected ? (
          <div className="flex items-center justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-sm font-medium text-emerald-700 dark:text-emerald-300">
                ✓ Đã kết nối: {status.data.email}
              </div>
              <div className="mt-1 text-[11px] text-slate-500">
                {status.data.scopes.length} scope · từ{' '}
                {status.data.connectedAt
                  ? new Date(status.data.connectedAt).toLocaleDateString('vi')
                  : '?'}
              </div>
              <details className="mt-2 text-[11px] text-slate-500">
                <summary className="cursor-pointer">Scopes chi tiết</summary>
                <ul className="mt-1 list-disc pl-4">
                  {status.data.scopes.map((s) => (
                    <li key={s} className="font-mono">
                      {s}
                    </li>
                  ))}
                </ul>
              </details>
            </div>
            <button
              type="button"
              onClick={onDisconnect}
              className="btn-secondary text-rose-600 dark:text-rose-300"
              disabled={disconnect.isPending}
            >
              <Unlink className="h-4 w-4" />
              Ngắt kết nối
            </button>
          </div>
        ) : (
          <div>
            <p className="mb-3 text-sm text-slate-600 dark:text-slate-400">
              Kết nối Gmail + Calendar để:
            </p>
            <ul className="mb-4 ml-4 list-disc text-sm text-slate-700 dark:text-slate-300">
              <li>Briefing 7am sẽ tổng hợp email mới của 24h trước</li>
              <li>Telegram nhắn "mai 14h gặp X" → tự tạo Calendar event</li>
              <li>Dashboard hiện 3 sự kiện sắp tới</li>
            </ul>
            <button type="button" onClick={onConnect} className="btn-primary">
              <LinkIconSafe />
              Kết nối Google
            </button>
            <p className="mt-2 text-[11px] text-slate-500">
              Sẽ chuyển đến trang Google consent. Cấp quyền:{' '}
              <code>gmail.readonly</code> + <code>calendar.events</code>.
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

function LinkIconSafe() {
  // Imported as `Link as LinkIcon` from lucide above is ambiguous with wouter Link.
  // Use a small inline SVG to avoid name conflicts.
  return (
    <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
    </svg>
  );
}

function ChangePasswordSection() {
  const change = useChangePassword();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error('Mật khẩu mới và xác nhận không khớp');
      return;
    }
    if (newPassword.length < 12) {
      toast.error('Mật khẩu mới tối thiểu 12 ký tự');
      return;
    }
    try {
      await change.mutateAsync({ currentPassword, newPassword });
      toast.success('Đã đổi mật khẩu — các session khác đã đăng xuất');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
    } catch (err: unknown) {
      const m = err instanceof Error ? err.message : 'Lỗi không xác định';
      toast.error(m);
    }
  };

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title flex items-center gap-2">
          <Lock className="h-3.5 w-3.5" />
          Đổi mật khẩu
        </h2>
      </div>
      <div className="panel-body">
        <form onSubmit={onSubmit} className="max-w-md space-y-4">
          <div>
            <label className="label">Mật khẩu hiện tại</label>
            <input
              type="password"
              className="input"
              value={currentPassword}
              onChange={(e) => setCurrentPassword(e.target.value)}
              autoComplete="current-password"
              required
            />
          </div>
          <div>
            <label className="label">Mật khẩu mới (≥ 12 ký tự)</label>
            <input
              type="password"
              className="input"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </div>
          <div>
            <label className="label">Xác nhận mật khẩu mới</label>
            <input
              type="password"
              className="input"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              autoComplete="new-password"
              minLength={12}
              required
            />
          </div>
          <button type="submit" className="btn-primary" disabled={change.isPending}>
            {change.isPending ? '…' : 'Đổi mật khẩu'}
          </button>
          <p className="text-xs text-slate-500">
            Sau khi đổi, các session khác (iPhone, browser khác) sẽ tự đăng xuất. Phiên hiện tại
            giữ.
          </p>
        </form>
      </div>
    </section>
  );
}

function ApiKeysSection() {
  const keys = useApiKeys();
  const create = useCreateApiKey();
  const revoke = useRevokeApiKey();
  const [open, setOpen] = useState(false);
  const [revealedKey, setRevealedKey] = useState<{ name: string; plaintext: string } | null>(
    null,
  );

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title flex items-center gap-2">
          <KeyRound className="h-3.5 w-3.5" />
          API Keys
        </h2>
        <button type="button" className="btn-primary text-xs" onClick={() => setOpen(true)}>
          <Plus className="h-3.5 w-3.5" />
          Tạo key
        </button>
      </div>
      <div className="panel-body">
        <p className="mb-3 text-xs text-slate-500">
          Dùng cho iOS Shortcut, scripts external. Bearer token trong header{' '}
          <code className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] dark:bg-white/10">
            Authorization: Bearer myos_…
          </code>
        </p>

        {keys.isLoading ? (
          <p className="text-sm text-slate-500">Đang tải…</p>
        ) : keys.data && keys.data.length > 0 ? (
          <ul className="divide-y divide-slate-200 dark:divide-white/5">
            {keys.data.map((k) => (
              <li key={k.id} className="flex items-center justify-between py-3">
                <div>
                  <div className="text-sm font-medium text-slate-900 dark:text-slate-100">
                    {k.name}
                  </div>
                  <div className="mt-0.5 flex items-center gap-2 text-[11px] text-slate-500">
                    <span>created {new Date(k.createdAt).toLocaleDateString('vi')}</span>
                    {k.lastUsedAt && (
                      <span>· last used {new Date(k.lastUsedAt).toLocaleDateString('vi')}</span>
                    )}
                    {k.expiresAt && (
                      <span>· expires {new Date(k.expiresAt).toLocaleDateString('vi')}</span>
                    )}
                  </div>
                </div>
                <ConfirmDeleteButton
                  size="md"
                  label={`Revoke ${k.name}`}
                  disabled={revoke.isPending}
                  onConfirm={() => {
                    revoke.mutate(k.id, {
                      onSuccess: () => toast.success('Đã revoke'),
                      onError: (e) =>
                        toast.error(e instanceof Error ? e.message : 'Không revoke được'),
                    });
                  }}
                />
              </li>
            ))}
          </ul>
        ) : (
          <p className="text-sm text-slate-500">Chưa có API key nào</p>
        )}
      </div>

      <CreateApiKeyModal
        open={open}
        onClose={() => setOpen(false)}
        onCreated={(name, plaintext) => {
          setRevealedKey({ name, plaintext });
          setOpen(false);
        }}
        submitting={create.isPending}
        onSubmit={(input) => create.mutateAsync(input)}
      />

      <RevealKeyModal
        keyData={revealedKey}
        onClose={() => setRevealedKey(null)}
      />
    </section>
  );
}

function CreateApiKeyModal({
  open,
  onClose,
  onCreated,
  onSubmit,
  submitting,
}: {
  open: boolean;
  onClose: () => void;
  onCreated: (name: string, plaintext: string) => void;
  onSubmit: (input: {
    name: string;
    expiresInDays?: number;
  }) => Promise<{ data: { plaintext: string; name: string } }>;
  submitting: boolean;
}) {
  const [name, setName] = useState('iOS Shortcut');
  const [expiresInDays, setExpiresInDays] = useState<string>('');

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    try {
      const res = await onSubmit({
        name: name.trim(),
        expiresInDays: expiresInDays ? Number(expiresInDays) : undefined,
      });
      onCreated(res.data.name, res.data.plaintext);
      toast.success('Đã tạo API key');
    } catch {
      toast.error('Lỗi khi tạo key');
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Tạo API key mới"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Huỷ
          </button>
          <button
            type="submit"
            form="api-key-form"
            className="btn-primary"
            disabled={submitting}
          >
            {submitting ? '…' : 'Tạo'}
          </button>
        </>
      }
    >
      <form id="api-key-form" onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="label">Tên (mô tả nơi dùng)</label>
          <input
            className="input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="iPhone iOS Shortcut"
            required
            autoFocus
          />
        </div>
        <div>
          <label className="label">Hết hạn sau (ngày, tuỳ chọn)</label>
          <input
            type="number"
            min="1"
            max="3650"
            className="input"
            value={expiresInDays}
            onChange={(e) => setExpiresInDays(e.target.value)}
            placeholder="365 (1 năm) — bỏ trống = không hết hạn"
          />
        </div>
      </form>
    </Modal>
  );
}

function RevealKeyModal({
  keyData,
  onClose,
}: {
  keyData: { name: string; plaintext: string } | null;
  onClose: () => void;
}) {
  if (!keyData) return null;
  return (
    <Modal
      open={true}
      onClose={onClose}
      title={`🔑 ${keyData.name} — copy NGAY`}
      footer={
        <button type="button" className="btn-primary" onClick={onClose}>
          Tôi đã lưu
        </button>
      }
    >
      <div className="space-y-3">
        <div className="rounded-xl border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 dark:border-amber-400/20 dark:bg-amber-500/10 dark:text-amber-200">
          ⚠️ <strong>Lưu ngay</strong> — token plaintext này sẽ KHÔNG hiển thị lại sau khi đóng
          modal. Server chỉ giữ hash.
        </div>
        <div className="relative">
          <pre className="overflow-x-auto rounded-xl border border-slate-300 bg-slate-100 p-3 font-mono text-xs text-slate-900 dark:border-white/10 dark:bg-white/[0.05] dark:text-slate-100">
            {keyData.plaintext}
          </pre>
          <button
            type="button"
            className="btn-icon absolute top-2 right-2"
            onClick={() => {
              navigator.clipboard.writeText(keyData.plaintext).then(
                () => toast.success('Đã copy'),
                () => toast.error('Không copy được'),
              );
            }}
            title="Copy"
          >
            <Copy className="h-4 w-4" />
          </button>
        </div>
        <details className="text-xs text-slate-600 dark:text-slate-400">
          <summary className="cursor-pointer font-semibold">
            Cách dùng cho iOS Shortcut
          </summary>
          <div className="mt-2 space-y-2">
            <p>1. Mở app Shortcuts trên iPhone → Tạo shortcut mới</p>
            <p>
              2. Action: <strong>Get Contents of URL</strong>
            </p>
            <ul className="ml-4 list-disc">
              <li>
                URL:{' '}
                <code className="rounded bg-slate-200 px-1 dark:bg-white/10">
                  https://dev-myos.de/api/v1/capture/shortcut
                </code>
              </li>
              <li>Method: POST</li>
              <li>
                Headers: <code>Authorization: Bearer {keyData.plaintext.slice(0, 20)}…</code>
              </li>
              <li>
                Request Body (JSON):{' '}
                <code>{`{"content": "[Dictation]", "source": "shortcut"}`}</code>
              </li>
            </ul>
            <p>3. Add to Home Screen → 1 tap để capture từ iPhone</p>
          </div>
        </details>
      </div>
    </Modal>
  );
}
